// src/hooks/useVisitorCount.ts
import { useState, useEffect } from 'react';
import { VisitorService } from '../services/VisitorService';

interface VisitorCount {
  count: number;
  isLoading: boolean;
  error: string | null;
}

interface UseVisitorCountProps {
  type: 'project' | 'total';
  projectId?: string;
}

export const useVisitorCount = ({
  type,
  projectId,
}: UseVisitorCountProps): VisitorCount => {
  const [visitorData, setVisitorData] = useState<VisitorCount>({
    count: 0,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const initializeVisitorCount = async () => {
      try {
        if (type === 'project' && projectId) {
          console.log('Initializing project visitor count for:', projectId);

          // Track the visit
          await VisitorService.trackVisit(projectId);

          // Subscribe to visit updates
          unsubscribe = VisitorService.getProjectVisits(projectId, count => {
            console.log('Received project visit count:', count);
            setVisitorData({
              count,
              isLoading: false,
              error: null,
            });
          });
        } else if (type === 'total') {
          console.log('Initializing total visitor count');

          unsubscribe = VisitorService.getTotalVisits(count => {
            console.log('Received total visit count:', count);
            setVisitorData({
              count,
              isLoading: false,
              error: null,
            });
          });
        }
      } catch (error) {
        console.error('Error in useVisitorCount:', error);
        setVisitorData(prev => ({
          ...prev,
          isLoading: false,
          error: 'Failed to track/fetch visits',
        }));
      }
    };

    initializeVisitorCount();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [type, projectId]);

  return visitorData;
};
